import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Download, ShieldCheck, FileImage, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, fileUrl, formatApiErrorDetail } from "../lib/api";

export default function PhotoDetail() {
  const { id } = useParams();
  const [photo, setPhoto] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    API.get(`/photos/${id}`)
      .then((r) => setPhoto(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  const buy = async () => {
    setBuying(true);
    try {
      const { data } = await API.post("/payments/checkout", {
        photo_id: photo.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBuying(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 pt-20" data-testid="photo-not-found">
        <p className="text-zinc-500 text-sm tracking-[0.2em] uppercase">This photograph is no longer available</p>
        <Link to="/gallery" className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] hover:text-white transition-colors duration-300">
          Back to the gallery
        </Link>
      </div>
    );
  }

  if (!photo) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20" data-testid="photo-loading">
        <Loader2 className="animate-spin text-zinc-600" size={28} />
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen" data-testid="photo-detail-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-12 md:py-20">
        <Link
          to="/gallery"
          data-testid="back-to-gallery"
          className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-zinc-500 hover:text-white transition-colors duration-300 mb-12"
        >
          <ArrowLeft size={14} /> Gallery
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <div className="relative border border-white/10 bg-[#0A0A0A] p-3 md:p-5" data-testid="photo-frame">
              <div className="overflow-hidden">
                <img
                  src={fileUrl(photo.image_url)}
                  alt={photo.title}
                  className="w-full h-auto block"
                />
              </div>
              <div className="absolute -top-px -left-px w-8 h-8 border-t-2 border-l-2 border-[#D4AF37]" />
              <div className="absolute -bottom-px -right-px w-8 h-8 border-b-2 border-r-2 border-[#D4AF37]" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 flex flex-col"
          >
            <span className="text-xs tracking-[0.25em] uppercase font-medium text-[#D4AF37] mb-4" data-testid="photo-category">
              {photo.category}
            </span>
            <h1 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tighter text-white" data-testid="photo-title">
              {photo.title}
            </h1>
            <p className="mt-6 text-base text-zinc-400 leading-relaxed" data-testid="photo-description">
              {photo.description}
            </p>

            <div className="mt-10 pt-8 border-t border-white/10">
              <div className="flex items-baseline gap-3 mb-8">
                <span className="font-display text-4xl md:text-5xl text-white" data-testid="photo-price">
                  ${photo.price.toFixed(0)}
                </span>
                <span className="text-xs tracking-[0.2em] uppercase text-zinc-500">AUD — Digital Edition</span>
              </div>
              <button
                onClick={buy}
                disabled={buying}
                data-testid="buy-photo-button"
                className="w-full inline-flex items-center justify-center gap-3 bg-white text-black text-xs tracking-[0.25em] uppercase font-semibold px-10 py-6 hover:bg-[#D4AF37] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {buying ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />}
                {buying ? "Preparing checkout" : "Buy the full-resolution file"}
              </button>
              <p className="mt-4 text-xs text-zinc-600" data-testid="promo-hint">Have a discount code? You can apply it at checkout.</p>
            </div>

            <ul className="mt-10 space-y-4">
              {[
                [FileImage, "High-resolution digital file, instant download"],
                [ShieldCheck, "Personal & commercial licence included"],
                [Download, "Yours forever — download any time from your receipt"],
              ].map(([Icon, text]) => (
                <li key={text} className="flex items-center gap-4 text-sm text-zinc-400">
                  <Icon size={16} className="text-[#D4AF37] shrink-0" />
                  {text}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
